/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Phone as PhoneIcon,
  MessageCircle,
  Mail,
  MapPin,
  Clock,
  ThumbsUp,
  Pencil,
  Trash2,
  CheckCircle2,
  Tag,
} from "lucide-react";
import StarRating from "./StarRating";
import ReviewModal from "./ReviewModal";
import { directoryApi, formatPhone, mailLink, telLink, waLink } from "./api";
import { useAuth } from "../auth/AuthContext";
import "./ContactDetail.css";

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffS = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (diffS < 60) return `${diffS}s ago`;
  const m = Math.floor(diffS / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setContact(await directoryApi.getContact(id));
    } catch (e) {
      setError(e.message || "Could not load contact");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const onSaved = () => {
    setReviewOpen(false);
    load();
  };

  const onDeleteContact = async () => {
    if (!window.confirm(`Delete contact "${contact.name}"? This can't be undone.`))
      return;
    setDeleting(true);
    try {
      await directoryApi.deleteContact(id);
      navigate("/directory");
    } catch (e) {
      setError(e.message || "Could not delete");
    } finally {
      setDeleting(false);
    }
  };

  const onDeleteMyReview = async () => {
    if (!window.confirm("Delete your review?")) return;
    try {
      await directoryApi.deleteMyReview(id);
      load();
    } catch (e) {
      setError(e.message || "Could not delete review");
    }
  };

  const onDeleteAdminReview = async (reviewId, reviewerName) => {
    if (!window.confirm(`Delete review by ${reviewerName || "this member"}?`)) return;
    try {
      await directoryApi.deleteReview(id, reviewId);
      load();
    } catch (e) {
      setError(e.message || "Could not delete review");
    }
  };

  if (loading) return <div className="vsd-detail-shell"><div className="vsd-empty">Loading…</div></div>;
  if (error)
    return (
      <div className="vsd-detail-shell">
        <div className="vsd-error">{error}</div>
        <Link to="/directory" className="vsd-back-link">
          <ArrowLeft size={14} /> Back to directory
        </Link>
      </div>
    );
  if (!contact) return null;

  const whatsapp = contact.whatsapp_phone || contact.primary_phone;
  const initials = (contact.name || "?").trim().split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="vsd-detail-shell">
      <div className="vsd-detail-topbar">
        <Link to="/directory" className="vsd-back-link">
          <ArrowLeft size={14} /> Back to directory
        </Link>
        {contact.can_edit && (
          <div className="vsd-detail-admin-actions">
            <button
              className="vsd-btn-secondary"
              onClick={() => navigate(`/directory/contact/${id}/edit`)}
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              className="vsd-btn-danger"
              onClick={onDeleteContact}
              disabled={deleting}
            >
              <Trash2 size={13} /> {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        )}
      </div>

      <div className="vsd-detail-grid">
        {/* LEFT CARD */}
        <div className="vsd-detail-left">
          <div className="vsd-detail-avatar">
            {contact.avatar_url ? (
              <img src={contact.avatar_url} alt={contact.name} />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <h1>{contact.name}</h1>
          <span className="vsd-detail-category">
            <Tag size={13} /> {contact.category?.name}
          </span>

          {contact.review_count > 0 ? (
            <div className="vsd-detail-rating">
              <StarRating
                value={contact.rating}
                count={contact.review_count}
                showCount
                readOnly
                size={20}
              />
            </div>
          ) : (
            <div className="vsd-detail-no-reviews">No reviews yet</div>
          )}

          {contact.is_community_pick && (
            <div className="vsd-detail-pick">
              <ThumbsUp size={14} strokeWidth={2.5} /> Community Pick
            </div>
          )}

          <div className="vsd-detail-info">
            <InfoRow icon={PhoneIcon} label="Phone">
              <a href={telLink(contact.primary_phone)} className="vsd-info-link">
                {formatPhone(contact.primary_phone)}
              </a>
            </InfoRow>
            {contact.secondary_phone && (
              <InfoRow icon={PhoneIcon} label="Secondary">
                <a href={telLink(contact.secondary_phone)} className="vsd-info-link">
                  {formatPhone(contact.secondary_phone)}
                </a>
              </InfoRow>
            )}
            {contact.email && (
              <InfoRow icon={Mail} label="Email">
                <a href={mailLink(contact.email)} className="vsd-info-link">
                  {contact.email}
                </a>
              </InfoRow>
            )}
            {contact.address && (
              <InfoRow icon={MapPin} label="Address">
                {contact.address}
              </InfoRow>
            )}
            {contact.working_hours && (
              <InfoRow icon={Clock} label="Working hours">
                {contact.working_hours}
              </InfoRow>
            )}
          </div>

          <div className="vsd-detail-actions">
            <a
              href={telLink(contact.primary_phone)}
              className="vsd-btn-primary vsd-btn-icon"
              aria-label="Call now"
              title="Call now"
            >
              <PhoneIcon size={18} />
            </a>
            <a
              href={waLink(whatsapp)}
              target="_blank"
              rel="noreferrer"
              className="vsd-btn-secondary vsd-btn-icon"
              aria-label="WhatsApp"
              title="WhatsApp"
            >
              <MessageCircle size={18} />
            </a>
          </div>

          <div className="vsd-detail-creator">
            Added by{" "}
            <strong>{contact.created_by?.name || "a member"}</strong>
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="vsd-detail-right">
          {contact.services_offered && (
            <section className="vsd-detail-card">
              <h2>Services offered</h2>
              <ul className="vsd-services">
                {contact.services_offered
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
              </ul>
            </section>
          )}

          {contact.description && (
            <section className="vsd-detail-card">
              <h2>About</h2>
              <p className="vsd-description">{contact.description}</p>
            </section>
          )}

          <section className="vsd-detail-card">
            <div className="vsd-reviews-head">
              <h2>
                {contact.review_count}{" "}
                {contact.review_count === 1 ? "Review" : "Reviews"}
              </h2>
              <div className="vsd-reviews-actions">
                {contact.my_review && (
                  <button
                    className="vsd-btn-ghost"
                    onClick={onDeleteMyReview}
                  >
                    <Trash2 size={12} /> Delete my review
                  </button>
                )}
                <button
                  className="vsd-btn-primary"
                  onClick={() => setReviewOpen(true)}
                >
                  {contact.my_review ? (
                    <>
                      <Pencil size={13} /> Edit your review
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={13} /> Write a review
                    </>
                  )}
                </button>
              </div>
            </div>

            {!contact.reviews || contact.reviews.length === 0 ? (
              <p className="vsd-no-reviews-yet">
                Be the first to share your experience!
              </p>
            ) : (
              <div className="vsd-review-list">
                {contact.reviews.map((r) => (
                  <article key={r.id} className="vsd-review">
                    <header>
                      <div>
                        <strong>{r.reviewer.name || "A member"}</strong>
                        {r.reviewer.villa && (
                          <span className="vsd-review-villa">
                            {" • "}
                            {r.reviewer.villa}
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="vsd-review-time">
                          {relativeTime(r.created_at)}
                        </span>
                        {isAdmin && (
                          <button
                            className="vsd-btn-ghost vsd-review-del-btn"
                            title="Delete review (admin)"
                            onClick={() => onDeleteAdminReview(r.id, r.reviewer.name)}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </header>
                    <StarRating value={r.rating} readOnly size={14} />
                    {r.content && <p>{r.content}</p>}
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <ReviewModal
        open={reviewOpen}
        contactId={id}
        existing={contact.my_review}
        contactName={contact.name}
        onClose={() => setReviewOpen(false)}
        onSaved={onSaved}
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="vsd-info-row">
      <Icon size={16} strokeWidth={1.8} />
      <div>
        <span className="vsd-info-label">{label}</span>
        <div className="vsd-info-value">{children}</div>
      </div>
    </div>
  );
}
